import { biliBridge, inBiliApp, isSupport } from '@bilibili/js-bridge'
import { getCookie, isAndroid, isNode, isArray } from './utils'
import { BRIDGE_METHOD, AUTH_METHOD, DEF_CONST } from './enum'

export function getAppUser() {
  return new Promise((resolve, reject) => {
    biliBridge.callNative({
      method: 'auth.getUserInfo',
      data: {
        type: 'default'
      },
      callback: (userInfo) => {
        try {
          if (userInfo.state.toString() === '0') {
            resolve(null)
            return
          }

          resolve({
            mid: userInfo.mid,
            avatar: userInfo.face,
            nickname: userInfo.userName
          })
        } catch (e) {
          reject(userInfo)
        }
      }
    })
  })
}

export function getCsrfToken(from) {
  return new Promise((resolve) => {
    if (isNode) {
      resolve('')
      return
    }

    const token = getCookie('bili_jct', from)
    if (!inBiliApp) {
      resolve(token)
      return
    }

    isSupport(BRIDGE_METHOD.getCsrf).then((support) => {
      if (!support) {
        resolve(token)
        return
      }

      biliBridge.callNative({
        method: BRIDGE_METHOD.getCsrf,
        callback: (info) => {
          resolve(info.csrf)
        }
      })
    })
  })
}

export function getDeviceInfo(option, config) {
  return new Promise((resolve) => {
    const injectDevice = getInjectDevice(option, config)

    if (isNode || !injectDevice) {
      resolve({})
      return
    }

    if (!inBiliApp) {
      resolve({
        buvid: getCookie('buvid3')
      })
      return
    }
    // 判断是否支持
    isSupport('global.getContainerInfo')
      .then((support) => {
        if (support) {
          biliBridge.callNative({
            method: 'global.getContainerInfo',
            callback: (info) => {
              const result = {
                ...info,
                device_id: info.fingerprint,
                build: info.build,
                buvid: info.buvid,
                device_name: decodeURIComponent(info.deviceName),
                device_platform: isAndroid ? info.devicePlatform : info.modelName,
                local_id: info.buvid,
                bili_local_id: info.localFingerprint
              }
              if (!isArray(injectDevice)) {
                resolve(result)
                return
              }
              const append = {}
              injectDevice.forEach((key) => {
                if (result[key]) {
                  append[key] = result[key]
                }
              })
              resolve(append)
            }
          })
        } else {
          resolve({})
        }
      })
      .catch(() => {
        resolve({})
      })
  })
}

export function getAuthType(option, config) {
  if (config.auth === AUTH_METHOD.all) {
    return AUTH_METHOD.all
  }

  if (config.auth === AUTH_METHOD.web) {
    return AUTH_METHOD.web
  }

  if (config.auth === AUTH_METHOD.app) {
    return AUTH_METHOD.app
  }

  if (option.auth === AUTH_METHOD.all) {
    return AUTH_METHOD.all
  }

  if (option.auth === AUTH_METHOD.web) {
    return AUTH_METHOD.web
  }

  if (option.auth === AUTH_METHOD.app) {
    return AUTH_METHOD.app
  }

  return AUTH_METHOD.all
}

export function getCsrfKey(option, config) {
  /**
   * 如果都没有设置，那就用默认值
   */
  if (typeof option.csrfKey === 'undefined' && typeof config.csrfKey === 'undefined') {
    return DEF_CONST.csrfKey
  }
  /**
   * 如果 option 设置了值，优先 option
   */
  if (typeof config.csrfKey !== 'undefined') {
    return config.csrfKey
  }
  /**
   * 其它 case
   * option 没设置，config 设置了
   */
  return option.csrfKey
}

export function getCsrfPos(option, config) {
  /**
   * 如果都没有设置，那就用默认值
   */
  if (typeof option.csrfPos === 'undefined' && typeof config.csrfPos === 'undefined') {
    return DEF_CONST.csrfPos.params
  }
  /**
   * 如果 option 设置了值，优先 option
   */
  if (typeof config.csrfPos !== 'undefined') {
    return config.csrfPos
  }
  /**
   * 其它 case
   * option 没设置，config 设置了
   */
  return option.csrfPos
}

export function getReferer(option, config) {
  /**
   * 如果都没有设置，那就用默认值
   */
  if (!isNode && typeof option?.headers?.referer === 'undefined' && typeof config?.headers?.referer === 'undefined') {
    return window.location.href
  }
  /**
   * 如果 option 设置了值，优先 option
   */
  if (typeof config?.headers?.referer !== 'undefined') {
    return config?.headers?.referer
  }
  /**
   * 其它 case
   * option 没设置，config 设置了
   */
  return option?.headers?.referer
}

export function getInjectDevice(option, config) {
  /**
   * 如果都没有设置，那就用默认值
   */
  if (typeof option.autoInjectDevice === 'undefined' && typeof config.autoInjectDevice === 'undefined') {
    return false
  }
  /**
   * 如果 option 设置了值，优先 option
   */
  if (typeof config.autoInjectDevice !== 'undefined') {
    return config.autoInjectDevice
  }
  /**
   * 其它 case
   * option 没设置，config 设置了
   */
  return option.autoInjectDevice
}

export function getTimeout(option, config) {
  return option.timeout || config.timeout || (isNode ? DEF_CONST.timeoutNode : DEF_CONST.timeout)
}

export function getBaseURL(option, config) {
  return config.baseURL || option.baseURL
}

export function getCacheName(option, config) {
  return config?.preload?.cacheName || option?.preload?.cacheName || window.location.pathname
}

export function getContentType(option, config, method) {
  if (config?.headers?.[[DEF_CONST.contentType.KEY]]) {
    return config.headers[[DEF_CONST.contentType.KEY]]
  }

  return DEF_CONST.contentType[method]
}

export function getBridgeName(option, config, method, runtime) {
  const contentType = getContentType(option, config, method)
  /**
   * 暂时还不支持上传图片
   */
  const isUploader = method === DEF_CONST.method.POST && contentType === DEF_CONST.contentType.FILE
  if (isUploader) {
    return ''
  }
  /**
   * 判断是否是 POST JSON 请求
   * v6 之后才支持了 bridge 发 POST JSON 的请求
   */
  const isJsonPost = method === DEF_CONST.method.POST && contentType === DEF_CONST.contentType.JSON
  if (isJsonPost) {
    if (runtime.version < 6) {
      return ''
    }

    return BRIDGE_METHOD.requestV2
  }

  const auth = getAuthType(option, config)
  /**
   * 当 auth 是 app 模式时，主要解决 sign 的问题
   * 那就按照版本来即可
   */
  if (auth === AUTH_METHOD.app) {
    if (runtime.version < 3) {
      return BRIDGE_METHOD.request
    }

    if (runtime.version < 6) {
      return BRIDGE_METHOD.requestWithSign
    }

    return BRIDGE_METHOD.requestWithSignV2
  }

  /**
   * 当 auth 是 web 的时候，主要解决的是掉登录的问题
   */
  if (auth === AUTH_METHOD.web) {
    const isOldMainApp = runtime.version < 6 && runtime.isMainApp
    /**
     * 对于 iOS 老版本的主 APP，因为存在 CSRF 和 SESSDATA 不同步的问题
     * 所以在 v6 之前哪怕走 bridge 发请求也会存在掉登录
     * 因此这个 case 就 fallback 吧
     */
    if (isOldMainApp && !isAndroid) {
      return ''
    }

    if (runtime.version < 3) {
      return BRIDGE_METHOD.request
    }

    if (runtime.version < 6) {
      return BRIDGE_METHOD.requestWithSign
    }

    return BRIDGE_METHOD.requestV2
  }

  /**
   * 哔哩哔哩漫画 Android、必剪、BBS 等一些 APP 还在使用老版本的 bridge，也想解决掉登录的问题
   * 他们可以使用老版本的 bridge 发请求解决问题，但是会存在额外参数覆盖的 case
   * 因为 bridge 版本恒定所以测试的时候就会发现问题
   */
  if (runtime.version < 3) {
    return BRIDGE_METHOD.request
  }

  if (runtime.version < 6) {
    return BRIDGE_METHOD.requestWithSign
  }

  return BRIDGE_METHOD.requestWithSignV2
}

export function getAppRequestData(option, config, runtime) {
  const data = {}
  if (runtime.version === 6 || runtime.version === 7) {
    const _ts = (Date.now() / 1000) | 0
    // 判断option全局配置和config单条request配置的优先权
    const getTargetConfig = config.noTsParams === undefined ? option : config
    if (!getTargetConfig.noTsParams) {
      data.ts = _ts
    }
  }
  return data
}
